import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { recipeAPI, boardAPI } from '../services/api';

function CreateFromIngredients({ user, onLogout }) {
  const navigate = useNavigate();
  const [ingredientsText, setIngredientsText] = useState('');
  const [loading, setLoading] = useState(false);
  const [recipes, setRecipes] = useState([]);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [boards, setBoards] = useState([]);
  const [saveModal, setSaveModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tweakModal, setTweakModal] = useState(null);
  const [tweakInstruction, setTweakInstruction] = useState('');
  const [tweaking, setTweaking] = useState(false);

  useEffect(() => {
    if (saveModal) {
      boardAPI.getAll().then((res) => setBoards(res.data.boards || [])).catch(() => setBoards([]));
    }
  }, [saveModal]);

  const parseIngredients = () => {
    const raw = ingredientsText.trim().replace(/,/g, '\n');
    const list = raw.split(/\n/).map((s) => s.trim()).filter(Boolean);
    return list;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const list = parseIngredients();
    if (list.length < 1) {
      setError('Enter at least 1 ingredient (6–10 recommended).');
      return;
    }
    if (list.length > 15) {
      setError('Please enter at most 15 ingredients.');
      return;
    }
    setError(null);
    setRecipes([]);
    setLoading(true);
    try {
      const res = await recipeAPI.suggestFromIngredients(list);
      setRecipes(res.data.recipes || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to get suggestions.');
      setRecipes([]);
    } finally {
      setLoading(false);
    }
  };

  const openSaveModal = (recipe) => {
    setSaveModal(recipe);
  };

  const closeSaveModal = () => {
    setSaveModal(null);
  };

  const getInstructionsList = (recipe) => {
    const inst = recipe.instructions;
    if (!inst || !inst.length) return [];
    return inst.map((s) => (typeof s === 'object' && s && s.instruction != null ? s.instruction : String(s)));
  };

  const getIngredientsList = (recipe) => {
    const ing = recipe.ingredients;
    if (!ing || !ing.length) return [];
    return ing.map((i) => (typeof i === 'object' && i && i.item != null ? i.item : String(i)));
  };

  const handleSaveRecipe = async (listId) => {
    if (!saveModal || !listId) return;
    setSaving(true);
    try {
      const instructions = getInstructionsList(saveModal);
      const ingredients = getIngredientsList(saveModal);
      await recipeAPI.create({
        list_id: listId,
        title: saveModal.title || 'Untitled Recipe',
        description: saveModal.description || null,
        servings: saveModal.servings || null,
        prep_time: saveModal.prep_time || null,
        cook_time: saveModal.cook_time || null,
        total_time: saveModal.total_time || null,
        ingredients,
        instructions,
      });
      closeSaveModal();
      navigate('/');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save recipe.');
    } finally {
      setSaving(false);
    }
  };

  const openTweakModal = (recipe, index) => {
    setTweakModal({ recipe, index });
    setTweakInstruction('');
  };

  const closeTweakModal = () => {
    if (!tweaking) {
      setTweakModal(null);
      setTweakInstruction('');
    }
  };

  const handleTweakSubmit = async () => {
    if (!tweakModal) return;
    setTweaking(true);
    try {
      const payload = {
        title: tweakModal.recipe.title,
        description: tweakModal.recipe.description,
        servings: tweakModal.recipe.servings,
        prep_time: tweakModal.recipe.prep_time,
        cook_time: tweakModal.recipe.cook_time,
        total_time: tweakModal.recipe.total_time,
        ingredients: getIngredientsList(tweakModal.recipe),
        instructions: getInstructionsList(tweakModal.recipe),
      };
      const res = await recipeAPI.customizeDraft(payload, tweakInstruction.trim() || undefined);
      setRecipes((prev) => prev.map((r, i) => (i === tweakModal.index ? res.data.recipe : r)));
      setExpandedId(tweakModal.index);
      closeTweakModal();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to tweak recipe.');
    } finally {
      setTweaking(false);
    }
  };

  return (
    <div className="app">
      <header className="header">
        <div className="header-content">
          <Link to="/" className="header-logo" style={{ textDecoration: 'none' }}>
            <img src="/logo.png" alt="RecipeWorks" className="header-logo-img" onError={(e) => { e.target.style.display = 'none'; }} />
          </Link>
          <div className="header-nav">
            <Link to="/" className="header-button" style={{ textDecoration: 'none' }}>
              ← Back to Dashboard
            </Link>
            <span style={{ marginRight: '1rem' }}>{user?.username}</span>
            <button className="header-button" onClick={onLogout}>Logout</button>
          </div>
        </div>
      </header>

      <div className="create-from-ingredients-page">
        <div className="create-from-ingredients-card">
          <h1 className="create-from-ingredients-title">Create a recipe for me</h1>
          <p className="create-from-ingredients-subtitle">
            Enter 6–10 ingredients you have (one per line or comma-separated). We’ll assume pantry staples like salt, pepper, flour, sugar, and common spices are available.
          </p>

          <form onSubmit={handleSubmit}>
            <label htmlFor="ingredients-input" className="create-from-ingredients-label">
              Your ingredients
            </label>
            <textarea
              id="ingredients-input"
              className="create-from-ingredients-textarea"
              placeholder={'e.g. chicken, broccoli, rice\nor one ingredient per line'}
              value={ingredientsText}
              onChange={(e) => setIngredientsText(e.target.value)}
              rows={6}
              disabled={loading}
            />
            {error && <div className="create-from-ingredients-error">{error}</div>}
            {loading && (
              <div className="create-from-ingredients-loading">
                <div className="loading-spinner" style={{ width: 32, height: 32, borderWidth: 4 }} aria-hidden />
                <span>AI is thinking…</span>
              </div>
            )}
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Getting ideas…' : 'Get recipe ideas'}
            </button>
          </form>

          {recipes.length > 0 && (
            <div className="create-from-ingredients-results">
              <h2>Recipe ideas</h2>
              {recipes.map((recipe, idx) => (
                <div key={idx} className="suggestion-card">
                  <div
                    className="suggestion-card-header"
                    onClick={() => setExpandedId(expandedId === idx ? null : idx)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setExpandedId(expandedId === idx ? null : idx)}
                  >
                    <span className="suggestion-card-title">{recipe.title}</span>
                    <span className="suggestion-card-toggle">{expandedId === idx ? '▼' : '▶'}</span>
                  </div>
                  <p className="suggestion-card-desc">{recipe.description}</p>
                  {recipe.servings && <span className="suggestion-card-meta">Servings: {recipe.servings}</span>}
                  {recipe.prep_time && <span className="suggestion-card-meta">Prep: {recipe.prep_time}</span>}
                  {recipe.cook_time && <span className="suggestion-card-meta">Cook: {recipe.cook_time}</span>}
                  {expandedId === idx && (
                    <div className="suggestion-card-body">
                      <h4>Ingredients</h4>
                      <ul>
                        {getIngredientsList(recipe).map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                      <h4>Instructions</h4>
                      <ol>
                        {getInstructionsList(recipe).map((step, i) => (
                          <li key={i}>{step}</li>
                        ))}
                      </ol>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => openTweakModal(recipe, idx)}
                        >
                          ✨ Tweak with AI
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => openSaveModal(recipe)}
                        >
                          Save to board
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {tweakModal && (
        <div className="modal-overlay" onClick={closeTweakModal} role="presentation" aria-modal="true">
          <div className="modal-content save-recipe-modal customize-modal" onClick={(e) => e.stopPropagation()}>
            <h3>✨ Tweak this suggestion with AI</h3>
            <p>We’ll tailor &quot;{tweakModal.recipe.title}&quot; to your tastes (savory-sweet, your condiments). Add any specific changes below, or leave blank.</p>
            <label htmlFor="tweak-instruction" className="create-from-ingredients-label">How would you like to customize? (optional)</label>
            <textarea
              id="tweak-instruction"
              className="create-from-ingredients-textarea"
              placeholder="e.g. make it spicier, less salt, add oyster sauce"
              value={tweakInstruction}
              onChange={(e) => setTweakInstruction(e.target.value)}
              rows={3}
              disabled={tweaking}
            />
            {tweaking && (
              <div className="create-from-ingredients-loading" style={{ marginBottom: '1rem' }}>
                <div className="loading-spinner" style={{ width: 24, height: 24, borderWidth: 3 }} aria-hidden />
                <span>AI is updating the recipe…</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-primary" onClick={handleTweakSubmit} disabled={tweaking}>
                {tweaking ? 'Updating…' : 'Update suggestion'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={closeTweakModal} disabled={tweaking}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {saveModal && (
        <div className="modal-overlay" onClick={closeSaveModal} role="presentation">
          <div className="modal-content save-recipe-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Save &quot;{saveModal.title}&quot; to a list</h3>
            <p>Choose a board and list to add this recipe to.</p>
            <div className="save-recipe-lists">
              {boards.map((board) => (
                <div key={board.id} className="save-recipe-board">
                  <strong>{board.title}</strong>
                  {(board.lists || []).map((list) => (
                    <button
                      key={list.id}
                      type="button"
                      className="btn btn-secondary save-recipe-list-btn"
                      onClick={() => handleSaveRecipe(list.id)}
                      disabled={saving}
                    >
                      {list.title}
                    </button>
                  ))}
                </div>
              ))}
            </div>
            {boards.length === 0 && !saving && <p>No boards found. Create a list on the dashboard first.</p>}
            <button type="button" className="btn btn-secondary" onClick={closeSaveModal}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateFromIngredients;
